import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Calendar,
  Users,
  Book,
  Bell,
  TrendingUp,
  ArrowRight,
  MapPin,
  Clock,
  Sparkles,
  Globe,
  Heart,
} from 'lucide-react'
import { useAuth } from '@/hooks/use-auth-wrapper'
import { mockEventos, mockMinisterios } from '@/mocks'
import { formatTime, formatCurrency } from '@/lib/utils'
import { useFinancasStore } from '@/stores/financas.store'
import { useOpenGraph } from '@/hooks/useOpenGraph'

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

export function DashboardPage() {
  const { perfil } = useAuth()
  const { saldoSeparado } = useFinancasStore()
  const proximosEventos = mockEventos.slice(0, 3)
  const meusMinisterios = mockMinisterios.slice(0, 2)

  // Configurar meta tags para compartilhamento social
  useOpenGraph({
    title: 'Dashboard',
    description: 'Acompanhe as atividades e informações da nossa igreja',
    type: 'website',
  })

  return (
    <div className="space-y-8">
      {/* Header com saudação */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
      >
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
          Olá, {perfil?.nome?.split(' ')[0] || 'Visitante'}! 👋
        </h1>
        <p className="text-muted-foreground">Aqui está um resumo das atividades da igreja</p>
      </motion.div>

      {/* Cards de estatísticas */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5"
      >
        <motion.div variants={item}>
          <Card className="overflow-hidden border-l-4 border-l-primary transition-all hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Próximos Eventos</CardTitle>
              <div className="rounded-full bg-primary/10 p-2">
                <Calendar className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{proximosEventos.length}</div>
              <p className="mt-1 flex items-center text-xs text-muted-foreground">
                <TrendingUp className="mr-1 h-3 w-3 text-emerald-500" />
                Esta semana
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card className="overflow-hidden border-l-4 border-l-secondary transition-all hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Ministérios Ativos</CardTitle>
              <div className="rounded-full bg-secondary/10 p-2">
                <Users className="h-4 w-4 text-secondary" />
              </div>
            </CardHeader>
            <CardContent>
              <span className="text-3xl font-bold">{meusMinisterios.length}</span>
              <p className="mt-1 text-xs text-muted-foreground">Você participa</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card className="overflow-hidden border-l-4 border-l-amber-500 transition-all hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Planos de Leitura</CardTitle>
              <div className="rounded-full bg-amber-500/10 p-2">
                <Book className="h-4 w-4 text-amber-500" />
              </div>
            </CardHeader>
            <CardContent>
              <span className="text-3xl font-bold">1</span>
              <p className="mt-1 text-xs text-muted-foreground">Em progresso</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card className="overflow-hidden border-l-4 border-l-emerald-500 transition-all hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Saldo Missões</CardTitle>
              <div className="rounded-full bg-emerald-500/10 p-2">
                <Globe className="h-4 w-4 text-emerald-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                {formatCurrency(saldoSeparado.missoes.saldo)}
              </div>
              <p className="mt-1 flex items-center text-xs text-muted-foreground">
                <Heart className="mr-1 h-3 w-3 text-emerald-500" />
                Disponível
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card className="overflow-hidden border-l-4 border-l-rose-500 transition-all hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avisos</CardTitle>
              <div className="rounded-full bg-rose-500/10 p-2">
                <Bell className="h-4 w-4 text-rose-500" />
              </div>
            </CardHeader>
            <CardContent>
              <span className="text-3xl font-bold">2</span>
              <p className="mt-1 flex items-center text-xs text-muted-foreground">
                <Sparkles className="mr-1 h-3 w-3 text-rose-500" />
                Novos
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      {/* Próximos Eventos */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="overflow-hidden">
          <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-transparent">
            <CardTitle className="text-xl">Próximos Eventos</CardTitle>
            <CardDescription className="mt-1">Não perca esses eventos especiais</CardDescription>
            <Button variant="ghost" size="sm" className="ml-auto">
              Ver todos
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {proximosEventos.map((evento, index) => (
                <motion.div
                  key={evento.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="group cursor-pointer p-6 transition-colors hover:bg-accent/50"
                >
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center rounded-lg bg-primary/10 px-3 py-2 text-center">
                      <span className="text-2xl font-bold text-primary">
                        {new Date(evento.inicio_em).getDate()}
                      </span>
                      <span className="text-xs uppercase text-muted-foreground">
                        {new Date(evento.inicio_em).toLocaleDateString('pt-BR', {
                          month: 'short',
                        })}
                      </span>
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold group-hover:text-primary">{evento.titulo}</h3>
                        <Badge variant="secondary">{evento.status}</Badge>
                      </div>
                      <p className="line-clamp-1 text-sm text-muted-foreground">
                        {evento.descricao}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>{formatTime(evento.inicio_em)}</span>
                        </div>
                        {evento.local && (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            <span>{evento.local}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Meus Ministérios e Leitura do Dia */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Meus Ministérios */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="h-full">
            <CardHeader className="border-b bg-gradient-to-r from-secondary/5 to-transparent">
              <CardTitle className="text-xl">Meus Ministérios</CardTitle>
              <CardDescription>Onde você está servindo</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {meusMinisterios.map((ministerio, index) => (
                <motion.div
                  key={ministerio.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="mb-4 flex items-center gap-4 rounded-lg border p-4 transition-all last:mb-0 hover:border-secondary hover:bg-secondary/5"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary/10">
                    <Users className="h-6 w-6 text-secondary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold">{ministerio.nome}</h4>
                    <p className="line-clamp-1 text-sm text-muted-foreground">
                      {ministerio.descricao}
                    </p>
                  </div>
                </motion.div>
              ))}
            </CardContent>
          </Card>
        </motion.div>

        {/* Leitura do Dia */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="h-full bg-gradient-to-br from-amber-50 to-transparent dark:from-amber-950/20">
            <CardHeader className="border-b">
              <div className="flex items-center gap-2">
                <Book className="h-5 w-5 text-amber-600 dark:text-amber-500" />
                <CardTitle className="text-xl">Leitura do Dia</CardTitle>
              </div>
              <CardDescription>Sua jornada espiritual</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="rounded-lg border border-amber-200 bg-white p-6 text-center dark:border-amber-900 dark:bg-amber-950/30">
                  <Book className="mx-auto mb-3 h-12 w-12 text-amber-600 dark:text-amber-500" />
                  <h3 className="mb-2 text-lg font-semibold">Salmos 23</h3>
                  <p className="text-sm italic text-muted-foreground">
                    "O Senhor é o meu pastor, nada me faltará..."
                  </p>
                </div>
                <Button className="w-full" variant="outline">
                  <Book className="mr-2 h-4 w-4" />
                  Começar Leitura
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}
